/**
 * Send a "QR did not open" ticket to your backend (Freshdesk proxy)
 * Keep this logic in one place so the screen stays clean.
 */
export async function sendQrNotOpenTicket({ details, apiBase, token }) {
    try {
        const safe = (v) =>
            String(v ?? "")
                .replaceAll("&", "&amp;")
                .replaceAll("<", "&lt;")
                .replaceAll(">", "&gt;")
                .replaceAll('"', "&quot;")
                .replaceAll("'", "&#39;");

        const dt = (ms) => (ms ? new Date(ms).toLocaleString() : "-");

        const list = (obj) =>
            Object.entries(obj || {})
                .map(([k, v]) => `<li><strong>${safe(k)}</strong>: ${safe(v)}</li>`)
                .join("");

        const renderCart = (items = []) =>
            items.length
                ? `<ul>${items
                    .map(
                        (x) =>
                            `<li>${safe(x.name || x.id)} — qty: ${safe(x.qty)}, price: ${safe(x.price)} SEK</li>`
                    )
                    .join("")}</ul>`
                : "<p>(empty)</p>";

        const consoleHtml = (details.consoleTail || [])
            .map((e) => `[${safe(dt(e.ts))}] ${safe(e.level)} — ${safe(e.msg)}`)
            .map((l) => `<li>${l}</li>`)
            .join("");

        const subject = `QR did not open — paymentId=${details?.journey?.paymentRequestId || "n/a"}`;

        const intro =
            `<p><strong>Automatiskt larm:</strong> QR-sidan öppnades inte inom timeout.</p>` +
            (details?.journey?.fridgeId ? `<p><strong>Enhet:</strong> ${safe(details.journey.fridgeId)}</p>` : "");

        const journey =
            `<h3>Transaktion</h3>` +
            `<ul>` +
            `<li><strong>Payment ID</strong>: ${safe(details?.journey?.paymentRequestId)}</li>` +
            `<li><strong>Fridge ID</strong>: ${safe(details?.journey?.fridgeId)}</li>` +
            `<li><strong>Belopp</strong>: ${safe(details?.journey?.amount)} SEK</li>` +
            `<li><strong>Användare</strong>: ${safe(details?.journey?.user?.id)}${
                details?.journey?.user?.email ? ` (${safe(details.journey.user.email)})` : ""
            }</li>` +
            `<li><strong>Starttid</strong>: ${safe(dt(details?.journey?.startedAt))}</li>` +
            `<li><strong>Deadline</strong>: ${safe(dt(details?.deadlineAt))}</li>` +
            `<li><strong>Fired</strong>: ${safe(dt(details?.firedAt))}</li>` +
            `</ul>` +
            `<h4>Kundvagn</h4>${renderCart(details?.journey?.cartItems)}`;

        const appCtx =
            `<h3>App</h3>` +
            `<ul>` +
            `<li><strong>Path</strong>: ${safe(details?.app?.path)}</li>` +
            `<li><strong>Visibility (armed)</strong>: ${safe(details?.app?.visibility)}</li>` +
            `<li><strong>Visibility (fired)</strong>: ${safe(details?.app?.visibilityAtFire)}</li>` +
            `</ul>` +
            `<h4>LocalStorage</h4>` +
            `<ul>${list(details?.app?.localStorage || {})}</ul>`;

        const device =
            `<h3>Enhet</h3>` +
            `<ul>` +
            `<li><strong>UA</strong>: ${safe(details?.device?.ua)}</li>` +
            `<li><strong>Platform</strong>: ${safe(details?.device?.platform)}</li>` +
            `<li><strong>Språk</strong>: ${safe(details?.device?.lang)}</li>` +
            `<li><strong>Viewport</strong>: ${safe(
                `${details?.device?.viewport?.w || "-"}x${details?.device?.viewport?.h || "-"}`
            )}</li>` +
            `<li><strong>Screen</strong>: ${safe(
                `${details?.device?.screen?.w || "-"}x${details?.device?.screen?.h || "-"} @${details?.device?.screen?.dpr || "-"}x`
            )}</li>` +
            (details?.device?.connection
                ? `<li><strong>Nät</strong>: ${safe(
                    `type=${details.device.connection.effectiveType}, downlink=${details.device.connection.downlink}, rtt=${details.device.connection.rtt}, saveData=${details.device.connection.saveData}`
                )}</li>`
                : "") +
            `</ul>`;

        const logs =
            `<h3>Console (senaste ${details?.consoleTail?.length || 0})</h3>` +
            (consoleHtml ? `<ol>${consoleHtml}</ol>` : "<p>(tomt)</p>");

        const htmlBody = `${intro}${journey}${appCtx}${device}${logs}`;

        const resp = await fetch(`${apiBase}/api/support/tickets`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ Subject: subject, HtmlBody: htmlBody }),
        });

        if (!resp.ok) {
            // optional: log text for diagnostics
            await resp.text().catch(() => "");
        }
    } catch {
        // swallow — ticketing must never break UX
    }
}
